import React, { Suspense, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import WindowSizeListener from 'react-window-size-listener';
import { useLocation, useNavigate } from 'react-router';
import { Button } from '@fluentui/react-components';
import { Home } from '@mui/icons-material';
import { ConsentDialog } from '@55degrees/consent-tracker';

import ChartsDropdown from './Menus/ChartsDropdown';
import LogoDisplay from './LogoDisplay';
import ExportDropdown from './Menus/ExportDropdown';
import LoadDataDropdown from './Menus/LoadDataDropdown';
import SettingsDropdown from './Menus/SettingsDropdown';
import DataSetInformation from './Informations/DataSetInformation';
import AccountDropdown from './Menus/AccountDropdown';
import { HeaderContainer, HeaderItemContainer, OptionsContainer } from './styling';
import BetaPageHeader from './BetaPageHeader';
import SelectedFiltersDialog from './Dialogs/SelectedFiltersDialog/SelectedFiltersDialog';
import useConsentProperty from '../../hooks/queries/useConsentProperty';
import DeleteDatasetDialog from './Dialogs/DeleteDatasetDialog/DeleteDatasetDialog';
import useSubscription from '../../hooks/useSubscription';
import { A3DocumentContext } from '../../contexts/A3DocumentContext';

const normalWindowWidth = 1160;

function Header({
  filters,
  setResetFiltersScope,
  openBillingPortalDialog,
  openManageUsersDialog,
  openFileLoader,
  loadingCounter,
  isDialogOpen,
  isDeleteDialogOpen,
  redirectToBetaPage,
  isBetaPage,
  setIsDarkMode,
  isDarkMode,
  openDataLoader,
  loadDatasetFromDropdown,
  isMixPanelInitialized,
  openDeleteDialog,
  closeDeleteDialog,
}) {
  const subscriptionQuery = useSubscription();
  const { isConsentDialogOpen, isFirstConsentSet, setIsFirstConsentSet, updateProperty } =
    useConsentProperty();
  const location = useLocation();
  const navigate = useNavigate();
  const { loadA3ExampleData } = useContext(A3DocumentContext);
  const [isNormalScreenSize, setisNormalScreenSize] = useState(true);
  const [isConsentOpen, setIsConsentOpen] = useState(isConsentDialogOpen);
  const [isDataSetDropdownOpen, setIsDataSetDropdownOpen] = useState(false);
  const [deleteDatasetName, setDeleteDatasetName] = useState('');
  const [deleteDatasetId, setDeleteDatasetId] = useState('');

  useEffect(() => {
    setIsConsentOpen(isConsentDialogOpen);
  }, [isConsentDialogOpen]);

  if (subscriptionQuery.isLoading || !subscriptionQuery.isSuccess) {
    return <HeaderContainer />;
  }

  const {
    data: { has_billing_access: hasBillingAccess, expired, isRestrictedBillingAccess },
  } = subscriptionQuery;

  const isRestrictedHeader = expired || isRestrictedBillingAccess;

  const storedConsent = JSON.parse(window.localStorage.getItem('55d-azure-user-tracking-consent'));
  const data = storedConsent?.value?.isEnabled;

  const isIndexPage = location.pathname === '/';

  const onHomeClick = () => {
    if (isIndexPage) return;
    navigate('/');
  };

  const handleOpenConsent = () => {
    setIsConsentOpen((prevState) => !prevState);
  };

  const onWindowSizeChange = (windowSize) => {
    const isNormalSize = windowSize.windowWidth > normalWindowWidth;
    setisNormalScreenSize(isNormalSize);
  };

  if (isBetaPage) {
    return <BetaPageHeader isNormalScreenSize scheme={isDarkMode ? 'Dark' : 'Light'} />;
  }

  const openDeleteDatasetDialog = (id, name) => {
    openDeleteDialog();
    setIsDataSetDropdownOpen(false);
    setDeleteDatasetId(id);
    setDeleteDatasetName(name);
  };

  const closeDeleteDatasetDialog = (loadExample = false) => {
    closeDeleteDialog();
    setDeleteDatasetId('');
    setDeleteDatasetName('');
    if (loadExample) {
      loadA3ExampleData();
    }
  };

  return (
    <WindowSizeListener onResize={onWindowSizeChange}>
      <HeaderContainer id="a3-header" isDarkMode={isDarkMode}>
        {/* LEFT - LOGO */}
        <LogoDisplay
          isNormalScreenSize={isNormalScreenSize}
          scheme={isDarkMode ? 'Dark' : 'Light'}
        />
        {/* LEFT CENTER - DATA SET */}
        {!isRestrictedHeader ? (
          <>
            <HeaderItemContainer>
              {isDeleteDialogOpen && (
                <Suspense fallback={null}>
                  <DeleteDatasetDialog
                    datasetId={deleteDatasetId}
                    datasetName={deleteDatasetName}
                    open={isDeleteDialogOpen}
                    handleClose={closeDeleteDatasetDialog}
                  />
                </Suspense>
              )}
              <LoadDataDropdown
                openFileLoader={openFileLoader}
                openDeleteDatasetDialog={openDeleteDatasetDialog}
                isNormalScreenSize={isNormalScreenSize}
                isDataSetDropdownOpen={isDataSetDropdownOpen}
                setIsDataSetDropdownOpen={setIsDataSetDropdownOpen}
                openDataLoader={openDataLoader}
                loadDatasetFromDropdown={loadDatasetFromDropdown}
              />
              {!isIndexPage && <DataSetInformation />}
              <SelectedFiltersDialog
                filters={filters}
                isDarkMode={isDarkMode}
                setResetFiltersScope={setResetFiltersScope}
              />
            </HeaderItemContainer>
            {/* RIGHT CENTER - CHARTS */}
            <HeaderItemContainer>
              <ChartsDropdown
                isNormalScreenSize={isNormalScreenSize}
                loadingCounter={loadingCounter}
                isDialogOpen={isDialogOpen}
              />
            </HeaderItemContainer>
          </>
        ) : null}
        {/* RIGHT - OPTIONS */}
        <OptionsContainer>
          {!isRestrictedHeader ? (
            <>
              <Button
                appearance="subtle"
                disabled={isIndexPage}
                icon={<Home />}
                id="a3-home-button"
                onClick={onHomeClick}
                size="large"
                shape="circular"
              />
              <ExportDropdown />
              <SettingsDropdown
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                redirectToBetaPage={redirectToBetaPage}
                handleOpenConsent={handleOpenConsent}
              />
              {isMixPanelInitialized && (
                <ConsentDialog
                  isConsentOpen={isConsentOpen}
                  setIsConsentOpen={setIsConsentOpen}
                  isFirstConsentSet={isFirstConsentSet}
                  setIsFirstConsentSet={setIsFirstConsentSet}
                  handleUpdateConsent={updateProperty}
                  isConsentGiven={data || false}
                  isDarkMode={isDarkMode}
                />
              )}
            </>
          ) : null}
          <AccountDropdown
            openBillingPortalDialog={openBillingPortalDialog}
            openManageUsersDialog={openManageUsersDialog}
            hasBillingAccess={hasBillingAccess}
          />
        </OptionsContainer>
      </HeaderContainer>
    </WindowSizeListener>
  );
}

Header.propTypes = {
  filters: PropTypes.objectOf(PropTypes.shape).isRequired,
  setResetFiltersScope: PropTypes.func.isRequired,
  openBillingPortalDialog: PropTypes.func.isRequired,
  openManageUsersDialog: PropTypes.func.isRequired,
  openFileLoader: PropTypes.func.isRequired,
  loadingCounter: PropTypes.number.isRequired,
  isDialogOpen: PropTypes.bool.isRequired,
  isDeleteDialogOpen: PropTypes.bool.isRequired,
  redirectToBetaPage: PropTypes.func.isRequired,
  isBetaPage: PropTypes.bool.isRequired,
  setIsDarkMode: PropTypes.func.isRequired,
  isDarkMode: PropTypes.bool.isRequired,
  openDataLoader: PropTypes.func.isRequired,
  loadDatasetFromDropdown: PropTypes.func.isRequired,
  isMixPanelInitialized: PropTypes.bool.isRequired,
  openDeleteDialog: PropTypes.func.isRequired,
  closeDeleteDialog: PropTypes.func.isRequired,
};

export default Header;
